import { supabase } from '@/lib/supabaseClient';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

interface AddPlayerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  playerName: string;
  onPlayerNameChange: (name: string) => void;
  onAddPlayer: () => void;
  teamName: string;
  whsId: string;
  onWhsIdChange: (id: string) => void;
  errorMessage?: string;
  clubName?: string;
  clubId?: string;
}

const AddPlayerDialog: React.FC<AddPlayerDialogProps> = ({
  isOpen,
  onOpenChange,
  playerName,
  onPlayerNameChange,
  onAddPlayer,
  teamName,
  whsId,
  onWhsIdChange,
  errorMessage,
  clubName,
  clubId
}) => {
  const [resolvedClubName, setResolvedClubName] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!resolvedClubName && clubId) {
      supabase
        .from('clubs')
        .select('name')
        .eq('id', clubId)
        .single()
        .then(({ data, error }) => {
          if (!error && data?.name) {
            setResolvedClubName(data.name);
          }
        });
    }
  }, [clubId, resolvedClubName]);

  const trimmedName = playerName.trim();
  const isWhsValid = /^\d{10}$/.test(whsId);
  const isFormValid = trimmedName.length > 0 && isWhsValid;

  const handleWhsIdChange = (value: string) => {
    const cleaned = value.replace(/[^0-9]/g, '').slice(0, 10);
    onWhsIdChange(cleaned);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {resolvedClubName ? 'Add New Club Player' : 'Add New Team Player'}
          </DialogTitle>
          <DialogDescription>
            This player will be associated directly to <strong>{clubName || resolvedClubName || teamName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="player-name">Player Name</Label>
            <Input
              id="player-name"
              value={playerName}
              onChange={(e) => onPlayerNameChange(e.target.value)}
              placeholder="Enter player's full name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="whs-id">WHS ID (10 digits)</Label>
            <Input
              id="whs-id"
              value={whsId}
              onChange={(e) => handleWhsIdChange(e.target.value)}
              placeholder="Enter 10-digit WHS ID"
              maxLength={10}
            />
            <p className="text-xs text-muted-foreground">
              {whsId.length}/10 digits
              {!isWhsValid && whsId.length > 0 && ' (must be exactly 10 digits)'}
            </p>
          </div>

          <p className="text-xs text-muted-foreground italic pt-1">
            Once added, a player’s name or WHS ID cannot be edited.
          </p>
        </div>

        {errorMessage && (
          <p className="text-sm text-destructive font-medium">{errorMessage}</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onAddPlayer} disabled={!isFormValid}>
            Add Player
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddClubPlayerDialog: React.FC<AddPlayerDialogProps> = ({
  isOpen,
  onOpenChange,
  playerName,
  onPlayerNameChange,
  onAddPlayer,
  whsId,
  onWhsIdChange,
  errorMessage,
  clubName,
  clubId
}) => {
  const [resolvedClubName, setResolvedClubName] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!resolvedClubName && clubId) {
      supabase
        .from('clubs')
        .select('name')
        .eq('id', clubId)
        .single()
        .then(({ data, error }) => {
          if (!error && data?.name) {
            setResolvedClubName(data.name);
          }
        });
    }
  }, [clubId, resolvedClubName]);

  const trimmedName = playerName.trim();
  const isWhsValid = /^\d{10}$/.test(whsId);
  const isFormValid = trimmedName.length > 0 && isWhsValid;

  const handleWhsIdChange = (value: string) => {
    const cleaned = value.replace(/[^0-9]/g, '').slice(0, 10);
    onWhsIdChange(cleaned);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Add New Club Player
          </DialogTitle>
          <DialogDescription>
            Enter the details of the new player to add to <strong>{resolvedClubName || '...'}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="player-name">Player Name</Label>
            <Input
              id="player-name"
              value={playerName}
              onChange={(e) => onPlayerNameChange(e.target.value)}
              placeholder="Enter player's full name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="whs-id">WHS ID (10 digits)</Label>
            <Input
              id="whs-id"
              value={whsId}
              onChange={(e) => handleWhsIdChange(e.target.value)}
              placeholder="Enter 10-digit WHS ID"
              maxLength={10}
            />
            <p className="text-xs text-muted-foreground">
              {whsId.length}/10 digits
              {!isWhsValid && whsId.length > 0 && ' (must be exactly 10 digits)'}
            </p>
          </div>

          <p className="text-xs text-muted-foreground italic pt-1">
            Once added, a player’s name or WHS ID cannot be edited.
          </p>
        </div>

        {errorMessage && (
          <p className="text-sm text-destructive font-medium">{errorMessage}</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onAddPlayer} disabled={!isFormValid}>
            Add Player
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { AddPlayerDialog, AddClubPlayerDialog };